<template>
  <div class="one-screen-analytic-messages">
    <SpinnerLoading v-if="isLoading" class="loader_center" color="blue" />

    <template v-else-if="messages.length > 0">
      <component
        :is="analyticMessageViewComponents[message.analyticType]"
        v-for="(message, messageIndex) in messages"
        :key="messageIndex"
        :analytic-message="message"
        class="one-screen-analytic-messages__analytic-message"
        @play-event-start="$emit('play-event-start', $event)"
        @show-full-screenshot="$emit('show-full-screenshot', $event)"
      />
    </template>

    <template v-else>
      <br>
      <p>{{ $t('noEvents') }}</p>
    </template>
  </div>
</template>

<script>
import {CameraInfo} from "@/store/cameras/index.js";
import {ACTION_LOAD_ARCHIVE_EVENTS_BY_CAMERA} from "@/store/analytics/index.js";
import {analyticMessageViewMixin} from "@/components/events/mixins.js";
import {ANALYTICS} from "@/store/analytics/helpers.js";
import {ACTION_LOAD_PARKING_DETECTION_EVENT} from "@/store/analytics/parkingDetections/index.js";
/**
 * Компонент для отображения списка архивных событий (сообщений).
 * Применяется для отображения событий аналитики камер за определенный период времени и для перехода ко времени наступления этих событий.
 */
export default {
  name: "ArchiveEvents",
  mixins: [
    analyticMessageViewMixin,
  ],
  props: {
    /**
     * Экземпляр {@link CameraInfo} по которому отображается информация в компоненте.
     */
    cameraInfo: {
      type: CameraInfo,
      required: true
    },
    /**
     * Начало отрезка времени для запроса архивных событий.
     */
    archiveFrom: {
      type: Date,
      default: null,
    },
    /**
     * Конец отрезка времени для запроса архивных событий.
     */
    archiveTo: {
      type: Date,
      default: null,
    },
  },
  data() {
    return {
      isLoading: false,
      messages: [],
    };
  },
  watch: {
    /**
     * Отслеживание изменений начала отрезка времени для запроса архива событий.
     *
     * @param {Date} newValue
     * @param {Date} oldValue
     */
    archiveFrom(newValue, oldValue) {
      if (+newValue !== +oldValue) {
        this.loadMessages();
      }
    },
    /**
     * Отслеживание изменений конца отрезка времени для запроса архива событий.
     *
     * @param {Date} newValue
     * @param {Date} oldValue
     */
    archiveTo(newValue, oldValue) {
      if (+newValue !== +oldValue) {
        this.loadMessages();
      }
    },
  },
  /**
   * Запрос архивных событий на начальном заданном отрезке времени.
   */
  mounted() {
    this.loadMessages();
  },
  methods: {
    /**
     * Запрашивает архивные события по камере на выбранном диапазоне времени
     * и складывает их в this.messages.
     *
     * Особый кейс: события парковки (parking_detection) - для них в общем списке
     * часто приходит "короткая" версия события без parkingSpots. Поэтому:
     * 1) берём последнее парковочное событие по дате,
     * 2) догружаем его детально по eventId,
     * 3) из детальной версии вытаскиваем parkingSpots и эмитим наверх,
     * 4) в список messages кладём только 1 парковочное событие (самое свежее),
     *    чтобы не плодить устаревшие парковочные события.
     */
    async loadMessages() {
      this.isLoading = true;
      this.messages = [];
      const messages = await this.$store.dispatch(
        `analytics/${ACTION_LOAD_ARCHIVE_EVENTS_BY_CAMERA}`, {
          cameraNumber: this.cameraInfo.number,
          archiveFrom: this.archiveFrom,
          archiveTo: this.archiveTo,
        }
      );
      // подмешиваем cameraInfo
      messages.forEach((analyticMessage) => {
        analyticMessage.cameraInfo = this.cameraInfo;
      });
      // ищем события парковки
      const parkingMessages = messages.filter(
        (m) => m.analyticType === ANALYTICS.parking_detection
      );
      if (parkingMessages.length > 0) {
        // находим последнее по дате
        const lastParkingMessage = parkingMessages.reduce((latest, msg) =>
          +msg.date > +latest.date ? msg : latest
        );
        let lastParkingWithSpots = lastParkingMessage;
        try {
          const detailedParkingEvents = await this.$store.dispatch(
            `analytics/parkingDetections/${ACTION_LOAD_PARKING_DETECTION_EVENT}`,
            {cameraNumber: this.cameraInfo.number, eventId: lastParkingWithSpots.id}
          );
          if (Array.isArray(detailedParkingEvents) && detailedParkingEvents.length > 0) {
            const detailed = detailedParkingEvents[0];
            detailed.cameraInfo = this.cameraInfo;
            lastParkingWithSpots = detailed;
            if (detailed.parkingSpots) {
              this.$emit("parking-spots-updated", detailed.parkingSpots);
            }
          }
          if (detailedParkingEvents && detailedParkingEvents.length > 0) {
            lastParkingWithSpots = detailedParkingEvents[0];
            lastParkingWithSpots.cameraInfo = this.cameraInfo;
          }
          if (detailedParkingEvents.parkingSpots) {
            this.$emit('parking-spots-updated', detailedParkingEvents.parkingSpots);
          }
        } catch (e) {
          console.error("Failed to load parking detection event", e);
        }
        // остальные события (других аналитик)
        const otherMessages = messages.filter(
          (m) => m.analyticType !== ANALYTICS.parking_detection
        );
        // собираем итоговый список и сортируем по дате (по убыванию)
        this.messages = [...otherMessages, lastParkingWithSpots].sort(
          (a, b) => +b.date - +a.date
        );
      } else {
        // обычный случай
        this.messages = messages;
      }

      this.isLoading = false;
    },
  }
};
</script>
